import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { isSignInWithEmailLink, signInWithEmailLink } from 'firebase/auth';
import { auth } from '../config/firebase.config';

export const EmailLinkConfirmation = () => {
  const [status, setStatus] = useState('checking');
  const [error, setError] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    handleEmailLinkSignIn();
  }, []);

  const handleEmailLinkSignIn = async () => {
    // Check if this is a sign-in with email link
    if (isSignInWithEmailLink(auth, window.location.href)) {
      // Get email from local storage or prompt user
      let email = window.localStorage.getItem('emailForSignIn');
      
      if (!email) {
        // User opened link on different device
        email = window.prompt('Please provide your email for confirmation');
      }

      if (email) {
        try {
          setStatus('signing-in');
          
          // Sign in with the email link
          const result = await signInWithEmailLink(auth, email, window.location.href);
          
          // Clear email from storage
          window.localStorage.removeItem('emailForSignIn');
          
          // Clear the URL to remove the sign-in link
          window.history.replaceState({}, document.title, window.location.pathname);
          
          console.log('✅ Successfully signed in:', result.user.email);
          
          // Log if this is a new user
          if (result._tokenResponse?.isNewUser) {
            console.log('🎉 New user created:', result.user.email);
          }
          
          setStatus('success');
          
          // Redirect to main app after 2 seconds
          setTimeout(() => {
            navigate('/');
          }, 2000);
          
        } catch (error) {
          console.error('❌ Error signing in with email link:', error);
          setError(getErrorMessage(error.code));
          setStatus('error');
        }
      } else {
        setError('Email is required to complete sign-in');
        setStatus('error');
      }
    } else {
      // Not a valid sign-in link
      setError('Invalid sign-in link');
      setStatus('error');
    }
  };

  const getErrorMessage = (errorCode) => {
    switch (errorCode) {
      case 'auth/expired-action-code':
        return 'This sign-in link has expired. Please request a new one.';
      case 'auth/invalid-action-code':
        return 'This sign-in link is invalid. Please request a new one.';
      case 'auth/user-disabled':
        return 'This account has been disabled.';
      case 'auth/invalid-email':
        return 'The email address is invalid.';
      default:
        return 'An error occurred during sign-in. Please try again.';
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <h1 className="text-center text-3xl font-extrabold text-gray-900">
          GOATnote EHR
        </h1>
        
        <div className="mt-8 bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
          {status === 'checking' && (
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
              <p className="mt-4 text-gray-600">Verifying your sign-in link...</p>
            </div>
          )}
          
          {status === 'signing-in' && (
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
              <p className="mt-4 text-gray-600">Signing you in...</p>
            </div>
          )}
          
          {status === 'success' && (
            <div className="text-center">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100">
                <svg className="h-6 w-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h3 className="mt-2 text-sm font-medium text-gray-900">Sign-in successful!</h3>
              <p className="mt-1 text-sm text-gray-500">Redirecting you to the app...</p>
            </div>
          )}
          
          {status === 'error' && (
            <div className="text-center">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100">
                <svg className="h-6 w-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </div>
              <h3 className="mt-2 text-sm font-medium text-gray-900">Sign-in failed</h3>
              <p className="mt-1 text-sm text-red-600">{error}</p>
              <button
                onClick={() => navigate('/')}
                className="mt-4 w-full inline-flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                Back to sign-in
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}; 